import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CommonDatagrid } from '../DataGrid';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { AnalyticsService, IAnalyticsQuery } from '../../api/services/analyticsService';
import {
  AnalyticsRow,
  IAnalyticsViewMeta,
  IAnalyticsViewResponse,
} from '../../types/api/response/analyticsResponse';
import { toGridColumn } from './format';
import { AsOfChip } from './AsOfChip';
import { describeAnalyticsError } from './errors';

interface IAnalyticsTableProps {
  /** Registered view name (from the analytics catalog), e.g. 'fresh-produce-writeoff'. */
  view: string;
  query?: IAnalyticsQuery;
  /** Client-side search field (must be one of the view's columns). */
  searchKey?: string;
  searchbarPlaceholder?: string;
  /** Bump to force a refetch (e.g. after a pipeline refresh completes). */
  reloadSignal?: number;
  onLoaded?: (response: IAnalyticsViewResponse) => void;
  hideAsOf?: boolean;
}

/**
 * THE generic report table: renders ANY registered analytics view from its catalog
 * metadata — columns, labels, type-aware formatting — with the standard as_of chip.
 * A future report costs zero table code: register the view, point this at it.
 */
export const AnalyticsTable = ({
  view,
  query,
  searchKey,
  searchbarPlaceholder,
  reloadSignal = 0,
  onLoaded,
  hideAsOf = false,
}: IAnalyticsTableProps) => {
  const dispatch = useDispatch();
  const [meta, setMeta] = useState<IAnalyticsViewMeta | null>(null);
  const [rows, setRows] = useState<AnalyticsRow[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalog, response] = await Promise.all([
        AnalyticsService.getCatalog(),
        AnalyticsService.queryView(view, query),
      ]);
      const viewMeta = catalog.find((v) => v.name === view) ?? null;
      setMeta(viewMeta);
      setRows(response.rows.map((row, index) => ({ ...row, __rowId: index })));
      setAsOf(response.as_of);
      onLoaded?.(response);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: describeAnalyticsError(e, { fallback: `Failed to load ${view}` }),
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, JSON.stringify(query), reloadSignal]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(() => (meta?.columns ?? []).map(toGridColumn), [meta]);

  return (
    <Box>
      {!hideAsOf && (
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <AsOfChip asOf={asOf} />
        </Box>
      )}
      <CommonDatagrid
        data={rows}
        columns={columns}
        rowId="__rowId"
        loading={loading}
        searchKey={searchKey}
        searchbarPlaceholder={searchbarPlaceholder ?? null}
      />
    </Box>
  );
};
